import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import Snackbar from './Snackbar';
import './Roles.css';

const CustomSearchDropdown = ({
  label,
  value,
  onChange,
  options,
  placeholder,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = options.filter((opt) =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="custom-dropdown-container">
      {label && <label className="modal-label">{label}</label>}

      <div
        className="custom-dropdown-box"
        onClick={() => setOpen(!open)}
      >
        <span className="dropdown-value">
          {value ? options.find((o) => o.value === value)?.label : placeholder}
        </span>
        <span className="dropdown-arrow">▼</span>
      </div>

      {open && (
        <div className="custom-dropdown-panel">
          <input
            type="text"
            value={search}
            className="dropdown-search-input"
            placeholder="Search..."
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="dropdown-options-list">
            {filtered.length > 0 ? (
              filtered.map((opt) => (
                <div
                  key={opt.value}
                  className={`dropdown-option ${
                    opt.value === value ? "selected" : ""
                  }`}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  {opt.label}
                </div>
              ))
            ) : (
              <div className="dropdown-no-results">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};



const Roles = () => {
  const navigate = useNavigate();

  const [employees, setEmployees] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedEmployeeEmail, setSelectedEmployeeEmail] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modals
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  // Form states
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newDept, setNewDept] = useState('');
  const [newRole, setNewRole] = useState('');
  const [newRoleId, setNewRoleId] = useState('');

  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editDept, setEditDept] = useState('');
  const [editDeptId, setEditDeptId] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editRoleId, setEditRoleId] = useState('');

  const [deleteId, setDeleteId] = useState(null);
  const [deleteEmail, setDeleteEmail] = useState('');

  const [isSaving, setIsSaving] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'success' });
  const showSnackbar = (msg, type = 'success') => setSnackbar({ open: true, message: msg, type });

  const getDepartmentId = async () => {
    const userDeptName = sessionStorage.getItem('user_department')?.trim();
    if (!userDeptName) return null;

    try {
      const deptData = await apiService.fetchData('metadata?type=departments');
      const matchedDept = deptData.data.find(
        d => d.DepartmentName.trim().toLowerCase() === userDeptName.toLowerCase()
      );
      return matchedDept ? matchedDept.DepartmentID : null;
    } catch (err) {
      console.error('Failed to fetch department ID', err);
      return null;
    }
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoading(true);
        const userEmail = sessionStorage.getItem('user_emailId');
        
        const encodedEmail = encodeURIComponent(userEmail);
        const response = await apiService.fetchData(`employees?role=manager&email=${encodedEmail}`);
        
        if (response && response.data) {
          const formattedData = response.data.map(emp => ({
            id: emp.ID,
            name: emp.FullName,
            email: emp.Email,
            department: emp.DepartmentName,
            role: emp.Role
          }));
          setEmployees(formattedData);
          setFiltered(formattedData);
        }
      } catch (err) {
        console.error('Error fetching employees:', err);
        
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    const result = employees.filter(e =>
      (e.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (e.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (e.department || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (e.role || '').toLowerCase().includes(searchQuery.toLowerCase())
    ).filter(e => 
      departmentFilter === 'All' || e.department === departmentFilter
    );
    setFiltered(result);
    setCurrentPage(1);
  }, [searchQuery, departmentFilter, employees]);

  const paginated = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  const totalPages = Math.ceil(filtered.length / rowsPerPage);

  // Get unique departments for filter
  const uniqueDepartments = ['All', ...new Set(employees.map(e => e.department).filter(Boolean))];

  const handleAdd = async () => {
    if (!selectedEmployeeEmail || !newRole) {
      return showSnackbar('Please select employee and role', 'error');
    }
    try {
      setIsSaving(true);
      const roleId = newRole === 'Admin' ? 1 : newRole === 'Manager' ? 2 : null;
      if (!roleId) {
        return showSnackbar('Invalid role selected', 'error');
      }
      const payload = {
        email: selectedEmployeeEmail,
        role_id: roleId
      };
      await apiService.postData('employees', payload);
      showSnackbar('Role assigned successfully', 'success');
      setIsAddOpen(false);
      resetAddForm();
    } catch (err) {
      console.error('Error assigning role:', err);
      showSnackbar('Failed to assign role', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!editEmail || !editRoleId) {
      return showSnackbar('Please select employee and role', 'error');
    }
    try {
      setIsUpdating(true);
      const selectedRole = roles.find(r => r.RoleID === parseInt(editRoleId));
      const roleName = selectedRole ? selectedRole.RoleName : '';
      
      if (!roleName) {
        return showSnackbar('Invalid role selected', 'error');
      }
      
      const encodedEmail = encodeURIComponent(editEmail);
      const encodedRole = encodeURIComponent(roleName);
      await apiService.putData(`employees?user_email=${encodedEmail}&role_name=${encodedRole}`, {});
      showSnackbar('Employee updated successfully', 'success');
      setIsEditOpen(false);
      
      // Refresh employee list
      const userEmail = sessionStorage.getItem('user_emailId');
      const response = await apiService.fetchData(`employees?role=manager&email=${encodeURIComponent(userEmail)}`);
      if (response && response.data) {
        const formattedData = response.data.map(emp => ({
          id: emp.ID,
          name: emp.FullName,
          email: emp.Email,
          department: emp.DepartmentName,
          role: emp.Role
        }));
        setEmployees(formattedData);
        setFiltered(formattedData);
      }
    } catch (err) {
      console.error('Error updating employee:', err);
      showSnackbar('Failed to update employee', 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteEmail) {
      return showSnackbar('Employee email not found', 'error');
    }
    try {
      const encodedEmail = encodeURIComponent(deleteEmail);
      await apiService.deleteData(`employees?user_email=${encodedEmail}`);
      showSnackbar('Employee deleted successfully', 'success');
      setIsDeleteOpen(false);
      
      // Refresh employee list
      const userEmail = sessionStorage.getItem('user_emailId');
      const response = await apiService.fetchData(`employees?role=manager&email=${encodeURIComponent(userEmail)}`);
      if (response && response.data) {
        const formattedData = response.data.map(emp => ({
          id: emp.ID,
          name: emp.FullName,
          email: emp.Email,
          department: emp.DepartmentName,
          role: emp.Role
        }));
        setEmployees(formattedData);
        setFiltered(formattedData);
      }
    } catch (err) {
      console.error('Error deleting employee:', err);
      showSnackbar('Failed to delete employee', 'error');
    }
  };

  const resetAddForm = () => {
    setNewName(''); setNewEmail(''); setNewDept(''); setNewRole(''); setNewRoleId(''); setSelectedEmployeeEmail('');
  };

  return (
    <div className="roles-container">
      {/* Header */}
      <div className="roles-header-section">
        <button className="roles-back-btn" onClick={() => navigate(-1)}>
          ← Back To Home
        </button>
        <div className="roles-title-wrapper">
          <h1 className="roles-page-title">Employee Management</h1>
          <p className="roles-page-subtitle">Manage employees, roles, and departments</p>
        </div>
        <div className="roles-action-buttons">
          <button className="roles-btn-primary" onClick={() => { 
            resetAddForm(); 
            setIsAddOpen(true);
          }}>
            + Assign Role
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="roles-search-section" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="roles-search-box" style={{width:'400px'}}>
          <input
            type="text"
            placeholder="Search by name, email, department, or role..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
          style={{
            padding: '10px 16px',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '14px',
            cursor: 'pointer',
            minWidth: '200px'
          }}
        >
          {uniqueDepartments.map(dept => {
            const deptCount = dept === 'All' ? employees.length : 
              employees.filter(e => e.department === dept).length;
            return (
              <option key={dept} value={dept}>
                {dept === 'All' ? 'All Departments' : dept} ({deptCount})
              </option>
            );
          })}
        </select>
      </div>

      {/* Table */}
      <div className="roles-table-wrapper">
        {loading ? (
          <div className="roles-loader-container">
    <div className="roles-spinner"></div>
    <p>Loading employees...</p>
  </div>
        ) : (
        <table className="roles-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Department</th>
              <th>Role</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((emp) => (
              <tr key={emp.id} className="roles-table-row">
                <td>{emp.id}</td>
                <td><strong>{emp.name}</strong></td>
                <td>{emp.email}</td>
                <td><span className="roles-dept-badge">{emp.department}</span></td>
                <td><span className="roles-role-badge">{emp.role}</span></td>
                <td className="roles-actions">
                  <button
                    onClick={async () => {
                      setEditId(emp.id);
                      setEditName(emp.name);
                      setEditEmail(emp.email);
                      setEditDept(emp.department);
                      setEditRole(emp.role);
                      setEditRoleId('');
                      setEditDeptId('');
                      setIsEditOpen(true);
                      
                      try {
                        const departmentId = await getDepartmentId();
                        if (!departmentId) {
                          showSnackbar('Could not determine your department.', 'error');
                          return;
                        }

                        const deptResponse = await apiService.fetchData('metadata?type=departments');
                        if (deptResponse && deptResponse.data) {
                          setDepartments(deptResponse.data);
                          const selectedDept = deptResponse.data.find(d => d.DepartmentName === emp.department);
                          if (selectedDept) {
                            setEditDeptId(selectedDept.DepartmentID);
                          }
                        }

                        const rolesResponse = await apiService.fetchData(`metadata?type=roles&department_id=${departmentId}`);
                        if (rolesResponse && rolesResponse.data) {
                          setRoles(rolesResponse.data);
                          const selectedRole = rolesResponse.data.find(r => r.RoleName === emp.role);
                          if (selectedRole) {
                            setEditRoleId(selectedRole.RoleID);
                          }
                        }
                      } catch (err) {
                        console.error('Error fetching metadata:', err);
                      }
                    }}
                    className="roles-icon-btn roles-edit-btn"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      setDeleteId(emp.id);
                      setDeleteEmail(emp.email);
                      setIsDeleteOpen(true);
                    }}
                    className="roles-icon-btn roles-delete-btn"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}

          {/* Pagination */}
          {!loading && (
          <div className="roles-pagination">
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>
                Showing {filtered.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, filtered.length)} of {filtered.length} entries
              </span>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{
                  padding: '4px 8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            {totalPages > 1 && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="roles-page-btn">
                  ← Previous
                </button>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          style={{
                            padding: '6px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            background: currentPage === page ? '#3b82f6' : 'white',
                            color: currentPage === page ? 'white' : '#374151',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return <span key={page} style={{ padding: '0 4px', color: '#6b7280' }}>...</span>;
                    }
                    return null;
                  })}
                </div>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="roles-page-btn">
                  Next →
                </button>
              </div>
            )}
          </div>
          )}
      </div>

      {/* Add Employee Modal */}
      {isAddOpen && (
        <div className="roles-modal-overlay" onClick={() => setIsAddOpen(false)}>
          <div className="roles-modal" onClick={e => e.stopPropagation()}>
            <h3 className="roles-modal-title">Assign New Role</h3>
            {/* <input placeholder="Full Name" value={newName} onChange={e => setNewName(e.target.value)} className="roles-input" />
            <input placeholder="Email" value={newEmail} onChange={e => setNewEmail(e.target.value)} className="roles-input" /> */}
          {/* <select
  value={selectedEmployeeEmail}
  onChange={(e) => setSelectedEmployeeEmail(e.target.value)}
  className="roles-select"
>
  <option value="">Select Employee</option>
  {employees.map(emp => (
    <option key={emp.id} value={emp.email}>
      {emp.name} – {emp.email}
    </option>
  ))}
</select> */}
<CustomSearchDropdown
  label="Select Employee"
  placeholder="Choose Employee"
  value={selectedEmployeeEmail}
  onChange={(v) => setSelectedEmployeeEmail(v)}
  options={employees.map((emp) => ({
    value: emp.email,
    label: `${emp.name} – ${emp.email}`,
  }))}
/>



            {/* <select value={newDept} onChange={e => setNewDept(e.target.value)} className="roles-select">
              <option value="">Select Department</option>
              {departments.map(d => <option key={d.DepartmentID} value={d.DepartmentName}>{d.DepartmentName}</option>)}
            </select> */}
            <CustomSearchDropdown
              label="Select Role"
              placeholder="Choose Role"
              value={newRole}
              onChange={(v) => setNewRole(v)}
              options={[
                { value: 'Admin', label: 'Admin' },
                { value: 'Manager', label: 'Manager' }
              ]}
            />
            <div className="roles-modal-actions">
              
              <button onClick={() => setIsAddOpen(false)} className="roles-btn-cancel" disabled={isSaving}>Cancel</button>
              <button onClick={handleAdd} className="roles-btn-gradient" disabled={isSaving}>{isSaving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Employee Modal */}
      {isEditOpen && (
        <div className="roles-modal-overlay" onClick={() => setIsEditOpen(false)}>
          <div className="roles-modal" onClick={e => e.stopPropagation()}>
            <h3 className="roles-modal-title">Edit Employee</h3>
            <CustomSearchDropdown
              label="Select Employee"
              placeholder="Choose Employee"
              value={editEmail}
              onChange={(v) => setEditEmail(v)}
              options={employees.map((emp) => ({
                value: emp.email,
                label: `${emp.name} – ${emp.email}`,
              }))}
            />
            <CustomSearchDropdown
              label="Select Department"
              placeholder="Choose Department"
              value={editDeptId}
              onChange={async (deptId) => {
                setEditDeptId(deptId);
                const dept = departments.find(d => d.DepartmentID === parseInt(deptId));
                setEditDept(dept?.DepartmentName || '');
                setEditRole('');
                if (deptId) {
                  try {
                    const rolesResponse = await apiService.fetchData(`metadata?type=roles&department_id=${deptId}`);
                    if (rolesResponse && rolesResponse.data) {
                      setRoles(rolesResponse.data);
                    }
                  } catch (err) {
                    console.error('Error fetching roles:', err);
                  }
                }
              }}
              options={departments.map((d) => ({
                value: d.DepartmentID,
                label: d.DepartmentName,
              }))}
            />
            <CustomSearchDropdown
              label="Select Role"
              placeholder="Choose Role"
              value={editRoleId}
              onChange={(v) => setEditRoleId(v)}
              options={roles.map((r) => ({
                value: r.RoleID,
                label: r.RoleName,
              }))}
            />
            <div className="roles-modal-actions">
             
              <button onClick={() => setIsEditOpen(false)} className="roles-btn-cancel" disabled={isUpdating}>Cancel</button>
               <button onClick={handleEdit} className="roles-btn-gradient" disabled={isUpdating}>{isUpdating ? 'Updating...' : 'Update'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {isDeleteOpen && (
        <div className="roles-modal-overlay" onClick={() => setIsDeleteOpen(false)}>
          <div className="roles-modal" onClick={e => e.stopPropagation()}>
            <h3 className="roles-modal-title">Delete Employee?</h3>
            <p className="role-delete-des" style={{padding:'10px'}}>Are you sure you want to permanently delete this employee?</p>
            <div className="roles-modal-actions">
              
              <button onClick={() => setIsDeleteOpen(false)} className="roles-btn-cancel">Cancel</button>
              <button onClick={handleDelete} className="roles-btn-danger">Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      {snackbar.message && snackbar.message.trim() && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          isOpen={snackbar.open}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          duration={3000}
        />
      )}
    </div>
  );
};

export default Roles;